import { SearchOrderSummary } from "../../models/ordering/search-order-summary";
import { PagedApiResult } from "../../models/utilities/paged-request";
import { ShipOrderCommand } from "../../pages/shipping/fedex/components/ship-order-command";
import { BaseService } from "../../services/_base-service";
import { Api, ApiResponse, StatusCode } from "../../utils/api";
import { ApiEndpoints } from "../../utils/app-constants";
import { ApproveOrdersCommand } from "./models/approve-orders-command";
import { BackorderCommandResult } from "./models/backorder-command-result";
import { AgingReport } from "./models/credit-hold-aging-report";
import { OrderSearchFilters } from "./models/order-search-filters";
import { OrdersEntered } from "./models/orders-entered";
import { SinglewideProduction } from "./models/singlewide-production";
import { WaitingOnSomeone } from "./models/waiting-on-someone";

export class OrderingSvc extends BaseService {

    public searchOrders = async (orderFilter : OrderSearchFilters) : Promise<ApiResponse<PagedApiResult<SearchOrderSummary>>> => {
        return await this.search(SearchOrderSummary, orderFilter);
    }

    /**
     * How many orders are waiting on someone to review or release them, and on what.  This is for a tile of the
     * dashboard, which says so itself when it can't be had, so it neither covers the page while it loads nor tells
     * the user when it fails.
     */
    public getWaitingOnSomeone = async () : Promise<ApiResponse<WaitingOnSomeone>> => {

        const response = new ApiResponse<WaitingOnSomeone>();

        try {
            const waiting = await Api.Get(WaitingOnSomeone, ApiEndpoints.DASHBOARD_WAITING);
            if (waiting) {
                response.data = waiting;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err as Error, true);
        }

        return response;
    }

    /**
     * The singlewides still to be produced on each line that assembles sections, by the week they are produced in.  This
     * is for a tile of the dashboard too, which says so itself when it can't be had.
     */
    public getSinglewideProduction = async () : Promise<ApiResponse<SinglewideProduction>> => {

        const response = new ApiResponse<SinglewideProduction>();

        try {
            const production = await Api.Get(SinglewideProduction, ApiEndpoints.DASHBOARD_SINGLEWIDES);
            if (production) {
                response.data = production;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err as Error, true);
        }

        return response;
    }

    /**
     * How many orders were entered on each of the last seven days, and on the same days the week before.  This is for a
     * tile of the dashboard too, which says so itself when it can't be had.
     */
    public getOrdersEntered = async () : Promise<ApiResponse<OrdersEntered>> => {

        const response = new ApiResponse<OrdersEntered>();

        try {
            const entered = await Api.Get(OrdersEntered, ApiEndpoints.DASHBOARD_ENTERED);
            if (entered) {
                response.data = entered;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err as Error, true);
        }

        return response;
    }

    /**
     * Get an order details, but leave it completely untyped
     * @param orderId The order ID to get details for
     */
    public getOrder = async (orderId: string) : Promise<ApiResponse<any>> => {

        const response = new ApiResponse<any>();
        this._showLoading();    

        if (orderId) {

            try {

                const order = await Api.UntypedGet(
                    ApiEndpoints.ORDERING_ORDER_BY_ID.formattify(orderId)
                );
                
                if (order) {
                    response.data = order;
                    response.status = StatusCode.Success;
                }
            }
            catch (err) {
                response.status = this._handleError(err);
            }
        }

        this._hideLoading();
        return response;
    }

    /**
     * Get an order history, but leave it completely untyped
     * @param orderId The order ID to get details for
     */
     public getOrderHistory = async (orderId: string) : Promise<ApiResponse<any>> => {

        const response = new ApiResponse<any>();
        this._showLoading();    

        if (orderId) {

            try {

                const order = await Api.UntypedGet(
                    ApiEndpoints.ORDERING_ORDER_HISTORY_BY_ID.formattify(orderId)
                );
                
                if (order) {
                    response.data = order;
                    response.status = StatusCode.Success;
                }
            }
            catch (err) {
                response.status = this._handleError(err);
            }
        }

        this._hideLoading();
        return response;
    }


    /**
     * Initiate the invoicing request for the provided order ID
     * @param orderId The order ID of the order you want to invoice
     * @returns True or false if the request succeeded or not
     */
    public shipOrder = async (order: ShipOrderCommand): Promise<ApiResponse<boolean>> => {

        const response = new ApiResponse<boolean>();

        try {
            const result = await this._post<number>(
                ApiEndpoints.ORDERING_SHIP_ORDER.formattify(order.orderId),
                order
            );

            response.status = result.status;
            if (result.status === StatusCode.Success) {
                response.data = true;
                this._showToast('The order was shipped successfully', 'success');
            }
        }
        catch (err) {
            response.status = this._handleError(err);
            response.data = false;
            this._showToast('An error occurred shipping that order', 'error');
        }

        return response;
    }

    /**
     * Initiate the invoicing request for the provided order ID
     * @param orderId The order ID of the order you want to invoice
     * @returns True or false if the request succeeded or not
     */
    public invoiceOrder = async (orderId : string) : Promise<ApiResponse<boolean>> => {

        const response = new ApiResponse<boolean>();

        try {
            const result = await this._post<number>(
                ApiEndpoints.ACCOUNTING_INVOICE_ORDER.formattify(orderId),
                {}
            );

            if (result) {
                response.status = StatusCode.Success;
                response.data   = true;
                this._showToast('The order was submitted successfully', 'success');
            }
        }
        catch (err) {
            response.status = this._handleError(err);
            response.data = false;
            this._showToast('An error occurred invoicing that order', 'error');
        }   

        return response;
    }

    /**
     * Start the backorder process for the provided order.  This removes the order
     * from the pickup/shipment/production run.
     * @param orderId The order ID of the order you want to invoice
     * @returns True or false if the request succeeded or not
     */
    public backorderOrder = async (orderId : string) : Promise<ApiResponse<BackorderCommandResult>> => {

        const response = new ApiResponse<BackorderCommandResult>();

        try {

            // Put all our print requests in a queue
            const result = await this._postWithError<BackorderCommandResult>(
                ApiEndpoints.ORDERING_BACKORDER.formattify(orderId),
                {}
            );

            const vmUpdate = new BackorderCommandResult();
            vmUpdate.wasAttempted = true;
            vmUpdate.orderId = orderId;

            if (result.status === StatusCode.Success) {
                vmUpdate.removedFromPickupId = result.data.removedFromPickupId;
                vmUpdate.removedFromDeliveryId = result.data.removedFromDeliveryId;
                vmUpdate.removedFromProductionRunId = result.data.removedFromProductionRunId;
                vmUpdate.sentToBackorderApp = result.data.sentToBackorderApp;
            }
            else {
                vmUpdate.error = result.data as unknown as string;  
                console.log(result);
            }

            response.status = StatusCode.Success;
            response.data = vmUpdate;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        return response;
    }

    /**
     * 
     * @param orderIds 
     * @returns 
     */
    public approveCreditHolds = async (orderIds : string[]) : Promise<ApiResponse<boolean>> => {

        const response = new ApiResponse<boolean>();

        try {

            const cmd = new ApproveOrdersCommand(orderIds);
             const result = await this._post<boolean>(
                ApiEndpoints.ORDERING_CREDIT_HOLD_APPROVE,
                cmd
            );

            if (result.status === StatusCode.Success) {
                response.status = StatusCode.Success;
                response.data   = true;
                this._showToast('Orders approved', 'success');
            }

        }
        catch (err) {
            response.status = this._handleError(err);
        }

        return response;
    }

    /**
     * Get the aging report for a dealer's orders
     * @param dealerId The dealer ID to get the aging report for
     */
    public getAgingReport = async (dealerId: string) : Promise<ApiResponse<AgingReport>> => {

        const response = new ApiResponse<AgingReport>();
        this._showLoading();    
    
        if (dealerId) {
    
            try {

                const report = await Api.Get<AgingReport>(
                    AgingReport,
                    ApiEndpoints.ORDERING_CREDIT_HOLD_AGING.formattify(dealerId)
                );
                    
                if (report) {
                    response.data = report;
                    response.status = StatusCode.Success;
                }
            }
            catch (err) {
                response.status = this._handleError(err);
            }
        }
    
        this._hideLoading();
        return response;
    }
}
